/**
 * Risk Disclaimer Page
 *
 * Public page (no authentication required)
 * Trading risk disclaimer for legal compliance
 */

import React from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import "./LegalPages.css";

export function RiskDisclaimerPage(): JSX.Element {
  return (
    <PublicLayout>
      <div className="legal-page">
      <div className="legal-page__container">
        <h1>Risk Disclaimer</h1>
        <p className="legal-page__last-updated">Last Updated: August 23, 2026</p>

        <section className="legal-page__section legal-page__section--warning">
          <h2>⚠️ Important Risk Warning</h2>
          <p>
            <strong>Trading cryptocurrencies involves substantial risk of loss and is not suitable for every investor.</strong>
            The high degree of leverage can work against you as well as for you. Before deciding to trade, you should carefully consider
            your investment objectives, level of experience, and risk appetite.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>1. No Guarantee of Profits</h2>
          <p>
            <strong>Zakyam AI Trader does not guarantee profits.</strong> Past performance is not indicative of future results.
            You may lose some or all of your invested capital. Only trade with funds you can afford to lose.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>2. Backtested vs. Live Results</h2>
          <p>
            Any performance figures, statistics, or averages shown on this site reflect <strong>historical backtesting or simulated
            performance</strong> over past periods, and are provided for illustrative and informational purposes only. They are
            <strong> not live trading results</strong>.
          </p>
          <p>
            Simulated or backtested results do not include real-world conditions such as live slippage, actual market liquidity,
            exchange fees, or order-fill delays. Because of these and other factors, actual live results may differ materially from
            backtested results, and the difference can be substantial. No representation is made that any account will achieve results
            similar to those shown.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>3. Automated Trading Risks</h2>
          <p>Automated trading systems carry specific risks:</p>
          <ul>
            <li><strong>Technical Failures:</strong> System errors, network issues, or exchange outages may prevent order execution</li>
            <li><strong>Market Volatility:</strong> Rapid price movements may result in unexpected losses</li>
            <li><strong>Liquidity Risks:</strong> Low liquidity may cause slippage or inability to exit positions</li>
            <li><strong>API Risks:</strong> Exchange API limitations or failures may affect trading</li>
            <li><strong>Account Restrictions:</strong> Your exchange may restrict, suspend, or terminate accounts that use third-party automation, depending on the exchange's own terms</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>4. Exchange Risks</h2>
          <p>
            Trading is executed through third-party exchanges (e.g., Bybit). We are not responsible for:
          </p>
          <ul>
            <li>Exchange downtime or technical issues</li>
            <li>Exchange policy changes</li>
            <li>Account restrictions or suspensions</li>
            <li>Losses due to exchange errors</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>5. API Key Security</h2>
          <p>
            You are responsible for securing your API keys. We recommend:
          </p>
          <ul>
            <li>Using Trade-only permissions (no Withdraw)</li>
            <li>Enabling IP whitelisting on your exchange account</li>
            <li>Regularly rotating your API keys</li>
            <li>Never sharing your API keys</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>6. No Financial Advice</h2>
          <p>
            The Service provides automated trading execution based on AI recommendations. <strong>This is not financial advice.</strong>
            You should consult with a qualified financial advisor before making trading decisions.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>7. Regulatory Compliance</h2>
          <p>
            Cryptocurrency trading may be subject to regulations in your jurisdiction. You are responsible for ensuring compliance
            with all applicable laws and regulations.
          </p>
        </section>

        <section className="legal-page__section legal-page__section--warning">
          <h2>8. Acknowledgment</h2>
          <p>
            By using Zakyam AI Trader, you acknowledge that you have read, understood, and agree to this Risk Disclaimer.
            You understand that trading involves risk, that past or backtested performance is not a guarantee of future results,
            and that you may lose your invested capital.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>9. Contact</h2>
          <p>
            If you have questions about the risks involved in trading, please contact us through the Support page.
          </p>
        </section>
      </div>
      </div>
    </PublicLayout>
  );
}

export default RiskDisclaimerPage;
